import React from "react";
import { View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { Position, ShiftForm } from "./types";
import { Menu } from "@/components/Menu";
import { PositionInput } from "@/components/inputs/PositionInput";
import { LookupInput, type LookupInputOption } from "@/components/ui/LookupInput";

interface PositionCardProps {
  position: Position;
  shiftIndex: number;
  positionIndex: number;
  shift: ShiftForm;
  updatePosition: (shiftIndex: number, positionIndex: number, patch: Partial<Position>) => void;
  removePosition: (shiftIndex: number, positionIndex: number) => void;
  duplicatePosition: (shiftIndex: number, positionIndex: number) => void;
  /** Location options for the LookupInput */
  locationOptions?: LookupInputOption[];
  /** Called when position location changes */
  onLocationChange?: (shiftIndex: number, positionIndex: number, locationId: string | null) => void;
}

export function PositionCard({
  position,
  shiftIndex,
  positionIndex,
  shift,
  updatePosition,
  removePosition,
  duplicatePosition,
  locationOptions = [],
  onLocationChange,
}: PositionCardProps) {
  const { colors } = useTheme();

  const hasOverride = !!position?.location_override;
  const shiftLocationName = shift.meta_data?.location?.name;

  return (
    <View
      style={{
        gap: 12,
        borderWidth: StyleSheet.hairlineWidth,
        borderColor: colors.border,
        borderRadius: 12,
        padding: 16,
        backgroundColor: colors.card,
      }}
    >
      {/* Position selection + actions */}
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}>
        <View style={{ flex: 1 }}>
          <PositionInput
            value={position.position_id || null}
            state="editing"
            onChange={(positionId) => {
              updatePosition(shiftIndex, positionIndex, {
                position_id: positionId ?? undefined,
              });
            }}
          />
        </View>
        <Menu
          buttonStyle={{
            borderWidth: 0,
            backgroundColor: "transparent",
          }}
          hideArchive={true}
          hideEdit={true}
          canDuplicate={true}
          canDelete={true}
          onDuplicate={() => duplicatePosition(shiftIndex, positionIndex)}
          onDelete={() => removePosition(shiftIndex, positionIndex)}
        />
      </View>

      {/* Quantity row */}
      {position.position_id && (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <ThemedText style={{ fontSize: 12, opacity: 0.7 }}>Needed:</ThemedText>
          <TextInput
            testID={`position-quantity-input-${shiftIndex}-${positionIndex}`}
            placeholder="1"
            placeholderTextColor={colors.placeholder}
            value={position?.quantity?.toString() ?? "1"}
            onChangeText={(text) => updatePosition(shiftIndex, positionIndex, { quantity: parseInt(text) || 1 })}
            keyboardType="numeric"
            style={{
              width: 50,
              height: 35,
              textAlign: "center",
              fontSize: 14,
              color: colors.text,
              borderWidth: StyleSheet.hairlineWidth,
              borderColor: colors.border,
              borderRadius: 8,
              backgroundColor: colors.backgroundSecondary,
            }}
          />
          <ThemedText style={{ fontSize: 12, opacity: 0.7 }}>Preferred:</ThemedText>
          <TextInput
            testID={`position-preferred-quantity-input-${shiftIndex}-${positionIndex}`}
            placeholder="1"
            placeholderTextColor={colors.placeholder}
            value={(position?.preferred_quantity || position?.quantity)?.toString() ?? "1"}
            onChangeText={(text) =>
              updatePosition(shiftIndex, positionIndex, { preferred_quantity: parseInt(text) || 1 })
            }
            keyboardType="numeric"
            style={{
              width: 50,
              height: 35,
              textAlign: "center",
              fontSize: 14,
              color: colors.text,
              borderWidth: StyleSheet.hairlineWidth,
              borderColor: colors.border,
              borderRadius: 8,
              backgroundColor: colors.backgroundSecondary,
            }}
          />
        </View>
      )}

      {/* Location */}
      <LookupInput
        label="Location"
        value={hasOverride ? position.location_override?.location?.id || null : null}
        options={locationOptions}
        state={hasOverride ? "editing" : "idle"}
        icon="map-marker"
        placeholder={!hasOverride && shiftLocationName ? shiftLocationName : "No location selected"}
        info={!hasOverride && shiftLocationName ? "From Shift" : !hasOverride ? "Inherits from shift" : undefined}
        onChange={(locationId) => {
          if (locationId) {
            onLocationChange?.(shiftIndex, positionIndex, locationId);
          } else {
            // Clear override, revert to inheriting
            updatePosition(shiftIndex, positionIndex, { location_override: undefined });
          }
        }}
      />
    </View>
  );
}
